import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Key, CheckCircle2, AlertTriangle, Upload, Image, X, Building2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { compressImage } from "../utils/imageCompressor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bank Transfer Info
const BANK_INFO = {
  bank: "BRI",
  accountNumber: "229201000104504",
  accountNumberFormatted: "2292 0100 0104 504",
  accountName: "FIO ALGUSTI.AR"
};

export default function InstallmentPayment() {
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Input Code, 2: Show Details & Transfer, 3: Upload Proof
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeData, setCodeData] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Masukkan kode cicilan");
      return;
    }

    setIsVerifying(true);
    try {
      const response = await axios.get(`${API}/customer/verify-code/${code.trim().toUpperCase()}`);
      setCodeData(response.data);
      setStep(2);
      toast.success("Kode valid!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kode tidak valid");
    } finally {
      setIsVerifying(false);
    }
  };

  const copyAccountNumber = () => {
    navigator.clipboard.writeText(BANK_INFO.accountNumber).then(() => {
      toast.success("Nomor rekening disalin!");
    }).catch(() => {
      const textArea = document.createElement("textarea");
      textArea.value = BANK_INFO.accountNumber;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success("Nomor rekening disalin!");
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("File harus berupa gambar");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Ukuran file maksimal 5MB");
        return;
      }
      setPaymentProof(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProof = () => {
    setPaymentProof(null);
    setPaymentProofPreview(null);
  };

  const handleSubmitPayment = async () => {
    if (!paymentProof) {
      toast.error("Upload bukti pembayaran terlebih dahulu");
      return;
    }

    setIsSubmitting(true);
    try {
      const base64Proof = await compressImage(paymentProof, 800, 0.5);
      
      const response = await axios.post(`${API}/customer/installment-payment`, {
        code: code.trim().toUpperCase(),
        payment_proof: base64Proof
      });
      
      toast.success("Pembayaran berhasil disubmit!");
      navigate(`/payment-status/${response.data.payment_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal submit pembayaran");
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen hero-gradient">
      {/* Header */}
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-xl mx-auto">
            <div className="mb-8 text-center">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">
                Bayar Cicilan
              </h1>
              <p className="text-slate-600">
                Masukkan kode dari admin untuk membayar tagihan cicilan
              </p>
            </div>

            {/* Steps indicator */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className={`flex items-center gap-2 ${step >= 1 ? "text-sky-600" : "text-slate-400"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? "bg-sky-600 text-white" : "bg-slate-200"}`}>
                  1
                </div>
                <span className="text-xs font-medium hidden sm:inline">Kode</span>
              </div>
              <div className="w-8 h-0.5 bg-slate-200" />
              <div className={`flex items-center gap-2 ${step >= 2 ? "text-sky-600" : "text-slate-400"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? "bg-sky-600 text-white" : "bg-slate-200"}`}>
                  2
                </div>
                <span className="text-xs font-medium hidden sm:inline">Transfer</span>
              </div>
              <div className="w-8 h-0.5 bg-slate-200" />
              <div className={`flex items-center gap-2 ${step >= 3 ? "text-sky-600" : "text-slate-400"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 3 ? "bg-sky-600 text-white" : "bg-slate-200"}`}>
                  3
                </div>
                <span className="text-xs font-medium hidden sm:inline">Kirim</span>
              </div>
            </div>

            {/* Step 1: Input Code */}
            {step === 1 && (
              <Card className="glass-card overflow-hidden">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
                      <Key className="w-8 h-8 text-sky-600" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                      Masukkan Kode Cicilan
                    </h3>
                    <p className="text-sm text-slate-600">
                      Kode diberikan oleh admin untuk pembayaran cicilan
                    </p>
                  </div>

                  <form onSubmit={handleVerifyCode} className="space-y-4">
                    <Input
                      type="text"
                      placeholder="Contoh: ABC123"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      className="h-14 text-center text-2xl font-mono tracking-widest uppercase"
                      maxLength={6}
                      data-testid="installment-code-input"
                    />
                    <Button
                      type="submit"
                      disabled={isVerifying || code.length < 6}
                      className="w-full h-14 rounded-full btn-flow"
                      data-testid="verify-code-btn"
                    >
                      {isVerifying ? (
                        <div className="spinner w-5 h-5" />
                      ) : (
                        "Verifikasi Kode"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Show Details & Transfer Info */}
            {step === 2 && codeData && (
              <Card className="glass-card overflow-hidden">
                <CardContent className="p-8">
                  {/* Code verified badge */}
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-emerald-600 font-medium">Kode Valid: {codeData.code}</span>
                  </div>

                  {/* Customer & Bill Details */}
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Pelanggan</span>
                        <span className="font-medium text-slate-800">{codeData.nama}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">No. Sambungan</span>
                        <span className="text-slate-800">{codeData.no_samb}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Periode</span>
                        <span className="text-slate-800">{codeData.periode_list}</span>
                      </div>
                    </div>
                  </div>

                  {/* Total Amount */}
                  <div className="bg-emerald-50 border-emerald-200 border rounded-xl p-4 mb-6 text-center">
                    <p className="text-sm text-emerald-700 mb-1">Total yang harus dibayar</p>
                    <p className="text-3xl font-heading font-bold text-emerald-600">
                      {formatCurrency(codeData.total_bayar)}
                    </p>
                  </div>

                  {/* Bank Transfer Info */}
                  <div className="mb-6">
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                        <Building2 className="w-7 h-7 text-emerald-600" />
                      </div>
                      <h3 className="text-lg font-heading font-semibold text-slate-800 mb-1">
                        Transfer Bank BRI
                      </h3>
                    </div>

                    <div className="bg-white border-2 border-emerald-200 rounded-2xl p-5 mb-4">
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-sm">BRI</span>
                        </div>
                        <span className="font-semibold text-slate-800">Bank BRI</span>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-4 mb-3">
                        <p className="text-xs text-slate-500 mb-1 text-center">Nomor Rekening</p>
                        <div className="flex items-center justify-center gap-2">
                          <p className="text-xl font-mono font-bold text-slate-800 tracking-wider">
                            {BANK_INFO.accountNumberFormatted}
                          </p>
                          <button
                            onClick={copyAccountNumber}
                            className="p-2 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-600 transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="text-center">
                        <p className="text-xs text-slate-500 mb-1">Atas Nama</p>
                        <p className="text-lg font-semibold text-slate-800">{BANK_INFO.accountName}</p>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      onClick={copyAccountNumber}
                      className="w-full h-12 rounded-xl gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      <Copy className="w-4 h-4" />
                      Salin Nomor Rekening
                    </Button>

                    <div className="flex items-start gap-3 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="text-amber-800 font-medium">Penting!</p>
                        <p className="text-amber-700">Transfer sesuai nominal. Simpan bukti transfer.</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => setStep(3)}
                    className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="next-to-upload-btn"
                  >
                    Saya Sudah Transfer
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Upload Bukti & Submit */}
            {step === 3 && codeData && (
              <Card className="glass-card overflow-hidden">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
                      <Upload className="w-8 h-8 text-sky-600" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">
                      Upload Bukti Pembayaran
                    </h3>
                    <p className="text-sm text-slate-600">
                      Upload screenshot atau foto bukti transfer
                    </p>
                  </div>

                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 mb-4 text-center">
                    <p className="text-sm text-sky-700">
                      Kode: <strong>{codeData.code}</strong> | Total: <strong>{formatCurrency(codeData.total_bayar)}</strong>
                    </p>
                  </div>

                  {!paymentProofPreview ? (
                    <div className="mb-6">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="proof-upload"
                        data-testid="proof-upload-input"
                      />
                      <label 
                        htmlFor="proof-upload" 
                        className="dropzone cursor-pointer flex flex-col items-center"
                      >
                        <div className="w-16 h-16 rounded-2xl bg-sky-100 flex items-center justify-center mb-4">
                          <Image className="w-8 h-8 text-sky-600" />
                        </div>
                        <p className="text-slate-800 font-medium mb-1">
                          Klik untuk upload bukti
                        </p>
                        <p className="text-sm text-slate-500">
                          JPG, PNG (Maks. 5MB)
                        </p>
                      </label>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <div className="relative inline-block w-full">
                        <img 
                          src={paymentProofPreview} 
                          alt="Bukti Pembayaran" 
                          className="w-full max-h-64 object-contain rounded-xl border border-slate-200"
                        />
                        <button
                          onClick={removeProof}
                          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-emerald-600 mt-2 text-center flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Bukti berhasil diupload
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="flex-1 h-12 rounded-full"
                    >
                      Kembali
                    </Button>
                    <Button
                      onClick={handleSubmitPayment}
                      disabled={!paymentProof || isSubmitting}
                      className="flex-1 h-12 rounded-full btn-flow"
                      data-testid="submit-payment-btn"
                    >
                      {isSubmitting ? (
                        <div className="spinner w-5 h-5" />
                      ) : (
                        "Kirim Pembayaran"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
